import { FC, useEffect, useRef, useState } from "react";
import Select, { components, GroupBase, OptionProps, SingleValueProps } from 'react-select';
import useStore from "Store";
import styled from "styled-components";
import { Image, ImageItem, Item, TemplateArea, TextItem, useZakeke, ZakekeDesigner } from "zakeke-configurator-react";
import { ReactComponent as Arrows } from '../../assets/icons/arrows-alt-solid.svg';
import { ReactComponent as Add } from '../../assets/icons/plus-circle-solid.svg';
import { ReactComponent as SearchMinusSolid } from '../../assets/icons/search-minus-solid.svg';
import { ReactComponent as SearchPlusSolid } from '../../assets/icons/search-plus-solid.svg';
import { T } from '../../Helpers';
import { Button, CarouselContainer, CloseEditorButton, Icon } from '../Atomic';
import AddTextDialog from "../dialogs/AddTextDialog";
import { useDialogManager } from "../dialogs/Dialogs";
import ErrorDialog from "../dialogs/ErrorDialog";
import ImagesGalleryDialog from "../dialogs/ImagesGalleryDialog";
import ItemImage, { EditImageItem } from "../widgets/ItemImage";
import ItemText, { EditTextItem } from "../widgets/ItemText";
import { Center, IconsAndDesignerContainer, SelectContainer, SupportedFormatsList, Template, TemplatesContainer, ZakekeDesignerContainer, ZoomInIcon, ZoomOutIcon } from "./LayoutStyled";

export type PropChangeHandler = (item: EditTextItem | EditImageItem, prop: string, value: string | boolean | File) => void;

const ZoomIconIn = styled(ZoomInIcon)`
    left:0px;
`
const ZoomIconOut = styled(ZoomOutIcon)`
    left:0px;
`

const DesignerContainer = styled.div<{ isMobile?: boolean }>`
    display:flex;
    flex-flow:column;
    user-select: none;
    width:100%;
    padding:30px 30px 70px 30px;
    ${props => props.isMobile && `
        position:fixed;
        top:0;
        left:0;
        width:100%;
        height:100%;
        z-index:11;
        background-color:#ffffff;
        overflow-y:scroll;
    `}
`;

const UploadButtons = styled.div`
  display:flex;
  flex-direction: column;
  grid-gap: 5px;  
  margin:20px 0px;
`;

const Area = styled.div<{ selected?: boolean }>`
  display:flex;
  flex-direction: column;
  justify-content: space-around;
  align-items:left;
  min-height:40px;
  border-bottom:5px solid transparent;
  cursor:pointer;
  
  &:hover{
    border-bottom: 5px solid #f5f6f7;
  }
  
  ${props => props.selected && `
       border-bottom: 5px solid #f5f6f7;
    `}
`;

const OptionContainer = styled(components.Option)`
    background-color: white !important;
    span {
        color:black;
    }
    &:hover {
        background-color: #DDD !important;
    }
`;

const SingleValueContainer = styled(components.SingleValue)`
    span {
        color:black;
    }
`;

const Designer: FC<{ isMobile?: boolean, onCloseClick?: () => void }> = ({ isMobile, onCloseClick }) => {

    const { showDialog, closeDialog } = useDialogManager();
    const [forceUpdate, setForceUpdate] = useState(false);
    const { setIsLoading } = useStore();

    const { currentTemplate, items, isAreaVisible, product, templates, setTemplate,
        removeItem, setItemImageFromFile, setItemImage, setItemText, setItemItalic, setItemBold, setItemColor,
        setItemFontFamily, setItemTextOnPath, addItemText, addItemImage, createImage, getTemplateUploadRestrictictions } = useZakeke();
    const customizerRef = useRef<any | null>(null);
    const [selectedCarouselSlide, setSelectedCarouselSlide] = useState<number>(0);

    const filteredAreas = product?.areas.filter(area => isAreaVisible(area.id)) ?? [];
    const [actualAreaId, setActualAreaId] = useState<number>(filteredAreas && filteredAreas.length > 0 ? filteredAreas[0].id : 0);
    const [moveElements, setMoveElements] = useState(false);

    const currentTemplateArea = currentTemplate!.areas.find(x => x.id === actualAreaId);
    const itemsFiltered = items.filter(item => item.areaId === actualAreaId);
    const allStaticElements = !itemsFiltered.some(item => {
        return !item.constraints || item.constraints.canMove || item.constraints.canRotate || item.constraints.canResize;
    });

    const showAddTextButton = !currentTemplateArea || currentTemplateArea.canAddText;
    const showUploadButton = !currentTemplateArea || (currentTemplateArea.canAddImage && currentTemplateArea.uploadRestrictions.isUserImageAllowed);
    const showGalleryButton = !currentTemplateArea || (currentTemplateArea.canAddImage && !currentTemplateArea.disableSellerImages);

    const supportedFileFormats = getSupportedUploadFileFormats(currentTemplate!.id, actualAreaId).join(', ');

    useEffect(() => {
        if (templates.length > 0 && !currentTemplate)
            setTemplate(templates[0].id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templates]);

    useEffect(() => {
        if (filteredAreas.length > 0 && actualAreaId === 0)
            setActualAreaId(filteredAreas[0].id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredAreas]);

    function getSupportedUploadFileFormats(templateId: number, areaId: number) {
        const restrictions = getTemplateUploadRestrictictions(templateId, areaId);
        const fileFormats: string[] = [];

        if (restrictions.isJpgAllowed)
            fileFormats.push(".jpg", ".jpeg");

        if (restrictions.isPngAllowed)
            fileFormats.push(".png");

        if (restrictions.isSvgAllowed)
            fileFormats.push(".svg");

        if (restrictions.isEpsAllowed)
            fileFormats.push(".eps");

        if (restrictions.isPdfAllowed)
            fileFormats.push(".pdf");

        return fileFormats;
    }

    const isItemEditable = (item: Item, templateArea?: TemplateArea) => {
        if (!item.constraints)
            return false;

        let {
            canEdit, canMove, canRotate, canResize, canDelete, canChangeFontColor, canChangeFontFamily,
            canChangeFontWeight, isPrintable
        } = item.constraints;

        if (!isPrintable)
            return false;

        let common = (canEdit || canMove || canRotate || canResize || canDelete);
        let text = (canChangeFontColor || canChangeFontFamily || canChangeFontWeight);
        let image = (canEdit || (templateArea && (templateArea.uploadRestrictions.isUserImageAllowed || !templateArea.disableSellerImages)));

        if (item.type === 0)
            return common || text;
        else
            return common || image;
    }


    const handleAddTextClick = () => {
        showDialog('add-text', <AddTextDialog
            onClose={() => closeDialog('add-text')}
            onConfirm={item => {
                addItemText(item, actualAreaId);
                closeDialog("add-text");
            }} />);
    }

    const handleAddImageFromGalleryClick = async () => {
        showDialog('add-image', <ImagesGalleryDialog
            onClose={() => closeDialog("add-image")}
            onImageSelected={(image: { imageID: number; }) => {
                addItemImage(image.imageID, actualAreaId);
                closeDialog("add-image");
            }} />)
    }

    const handleUploadImageClick = async (addItemImage: (guid: any, imageId: number) => Promise<void>, createImage: (file: File, progress?: (percentage: number) => void) => Promise<Image>) => {
        if (currentTemplate && actualAreaId) {
            const fileFormats = getSupportedUploadFileFormats(currentTemplate.id, actualAreaId);
            let input = document.createElement("input");
            input.setAttribute("accept", fileFormats.join(","));
            input.setAttribute("type", "file");
            input.addEventListener("change", async (e) => {
                const files = (e.currentTarget as HTMLInputElement).files;
                if (files && files.length > 0 && actualAreaId) {
                    setIsLoading(true);
                    try {
                        const image = await createImage(files[0], (progress: number) => console.log(progress));
                        addItemImage(image.imageID, actualAreaId);
                        input.remove();
                    } catch (ex) {
                        console.error(ex);
                        showDialog('error', <ErrorDialog error={T._("Failed uploading image.", "Composer")} onCloseClick={() => closeDialog('error')} />);
                    } finally {
                        setIsLoading(false);
                    }
                }
            });
            document.body.appendChild(input);
            input.click();
        }
    }

    const handleItemRemoved = (guid: string) => {
        removeItem(guid);
    }

    const handleItemImageChange = async (item: EditImageItem, file: File) => {
        try {
            setIsLoading(true);
            await setItemImageFromFile(item.guid, file);
        } catch (ex) {
            console.error(ex);
        } finally {
            setIsLoading(false);
        }
    }

    const handleItemImageGallery = async (item: EditImageItem) => {

        showDialog('add-image', <ImagesGalleryDialog
            onClose={() => closeDialog('add-image')}
            onImageSelected={async (image) => {
                closeDialog('add-image');
                try {
                    setIsLoading(true);
                    await setItemImage(item.guid, image.imageID);

                } catch (ex) {
                    console.error(ex);
                } finally {
                    setIsLoading(false);
                }
            }} />);
    }

    const handleItemPropChange = (item: EditTextItem | EditImageItem, prop: string, value: string | boolean | File) => {

        switch (prop) {
            case 'remove':
                handleItemRemoved(item.guid);
                break;
            case 'image-upload':
                handleItemImageChange(item as EditImageItem, value as File);
                break;
            case 'image-gallery':
                handleItemImageGallery(item as EditImageItem);
                break;
            case 'text':
                setItemText(item.guid, value as string);
                break;
            case 'font-italic':
                setItemItalic(item.guid, value as boolean);
                break;
            case 'font-bold':
                setItemBold(item.guid, value as boolean);
                break;
            case 'font-color':
                setItemColor(item.guid, value as string);
                break;
            case 'font-family':
                setItemFontFamily(item.guid, value as string);
                break;
            case 'text-path':
                setItemTextOnPath(item.guid, actualAreaId, value as boolean);
                setTimeout(() => setForceUpdate(!forceUpdate), 100);
                break;
        }
    }

    const SelectOption = (props: JSX.IntrinsicAttributes & OptionProps<any, boolean, GroupBase<any>>) => {
        return (
            <OptionContainer {...props}>
                {<span>{props.data.name}</span>}
            </OptionContainer>
        );
    }

    const SelectSingleValue = (props: JSX.IntrinsicAttributes & SingleValueProps<any, boolean, GroupBase<any>>) => {
        return (
            <SingleValueContainer {...props}>
                {<span>{props.data.name}</span>}
            </SingleValueContainer>
        );
    }

    return <>
        {!moveElements && <DesignerContainer isMobile={isMobile}>
            {/*templates*/}
            {!isMobile && templates.length > 1 &&
                <TemplatesContainer>
                    {templates.map(template => <Template
                        key={template.id}
                        selected={currentTemplate === template}
                        onClick={() => {
                            setTemplate(template.id);
                        }}
                    >
                        {template.name}
                    </Template>)}
                </TemplatesContainer>}
            {/* Areas */}
            {!isMobile && filteredAreas.length > 1 && <CarouselContainer slidesToScroll={1} speed={50}
                slidesToShow={window.innerWidth <= 1600 ? 3 : 4}
                slideIndex={selectedCarouselSlide}
                afterSlide={setSelectedCarouselSlide}
                renderBottomCenterControls={() => <span />}
                renderCenterRightControls={() => <span />}
                renderCenterLeftControls={() => <span />}>

                {filteredAreas.map(area => <Area
                    key={area.id}
                    selected={actualAreaId === area.id}
                    onClick={() => setActualAreaId(area.id)}>
                    {area.name}
                </Area>)}
            </CarouselContainer>}

            {isMobile && templates.length > 1 && <SelectContainer>
                <span>Template</span>
                <Select
                    styles={{
                        container: base => ({
                            ...base,
                            minWidth: 300,
                        }),
                    }}
                    isSearchable={false}
                    options={templates}
                    menuPosition="fixed"
                    components={{
                        Option: SelectOption,
                        SingleValue: SelectSingleValue
                    }}
                    value={[templates!.find(x => x === currentTemplate)]}
                    onChange={(template: any) => setTemplate(template.id)}
                />
            </SelectContainer>
            }
            {isMobile && filteredAreas.length > 1 &&
                <SelectContainer>
                    <span>Customizable Areas</span>
                    <Select
                        styles={{
                            container: base => ({
                                ...base,
                                minWidth: 300,
                            }),
                        }}
                        isSearchable={false}
                        options={filteredAreas}
                        menuPosition="fixed"
                        components={{
                            Option: SelectOption,
                            SingleValue: SelectSingleValue
                        }}
                        value={[filteredAreas!.find(x => x.id === actualAreaId)]}
                        onChange={(area: any) => setActualAreaId(area.id)}
                    />
                </SelectContainer>
            }

            {(itemsFiltered.length === 0 && !(showAddTextButton || showUploadButton || showGalleryButton)) && <Center>{T._("No customizable items", "Composer")}</Center>}

            {itemsFiltered.map(item => {
                if (item.type === 0 && isItemEditable(item, currentTemplateArea))
                    return <ItemText key={item.guid} handleItemPropChange={handleItemPropChange} item={item as TextItem} />
                else if (item.type === 1 && isItemEditable(item, currentTemplateArea))
                    return <ItemImage key={item.guid} handleItemPropChange={handleItemPropChange} item={item as ImageItem} currentTemplateArea={currentTemplateArea!} />

                return null;
            })}

            {(showAddTextButton || showUploadButton || showGalleryButton) && <UploadButtons>
                {showAddTextButton && <Button
                    isFullWidth
                    onClick={handleAddTextClick}>
                    <Icon><Add /></Icon>
                    <span>{T._("ADD TEXT", "Composer")}</span>
                </Button>}

                {showGalleryButton && <Button
                    isFullWidth
                    onClick={handleAddImageFromGalleryClick} >
                    <Icon><Add /></Icon>
                    <span>{T._("ADD CLIPART", "Composer")}</span>
                </Button>}

                {showUploadButton && <>
                    <Button
                        isFullWidth
                        onClick={() => handleUploadImageClick(addItemImage, createImage)}>
                        <Icon><Add /></Icon>
                        <span>
                            <span>{itemsFiltered.some(item => item.type === 1) ?
                                T._("UPLOAD ANOTHER IMAGE", "Composer") : T._("UPLOAD IMAGE", "Composer")} </span>
                        </span>
                    </Button>
                </>}
                <SupportedFormatsList>{T._("Supported file formats:", "Composer") + " " + supportedFileFormats}</SupportedFormatsList>
            </UploadButtons>}
            {itemsFiltered.length > 0 && !allStaticElements &&
                <Button
                    isFullWidth
                    outline
                    onClick={() => setMoveElements(true)}>
                    <Icon><Arrows /></Icon>
                    <span>{T._("MOVE ELEMENTS", "Composer")} </span>
                </Button>
            }
            {isMobile && <CloseEditorButton onClick={onCloseClick}>{T._("OK", "Composer")}</CloseEditorButton>}
        </DesignerContainer>}
        {moveElements && <ZakekeDesignerContainer isMobile={isMobile} className="zakeke-container">
            <ZakekeDesigner ref={customizerRef} areaId={actualAreaId} />
            <IconsAndDesignerContainer>
                <ZoomIconIn hoverable onClick={() => customizerRef.current.zoomIn()} ><SearchPlusSolid /></ZoomIconIn>
                <ZoomIconOut hoverable onClick={() => customizerRef.current.zoomOut()} ><SearchMinusSolid /></ZoomIconOut>

            </IconsAndDesignerContainer>
            <Button
                isFullWidth
                isMoveElements={moveElements}
                onClick={() => setMoveElements(false)}>
                <span>{T._("OK", "Composer")} </span>
            </Button>
        </ZakekeDesignerContainer>
        }
    </>
}

export default Designer;